import { useMemo, useState, type ReactNode } from "react";
import EmptyState from "@/components/base/EmptyState";
import ExportMenu from "@/components/base/ExportMenu";
import { Select } from "@/components/base/FormControls";
import TerritoryFilters from "@/components/base/TerritoryFilters";
import type { TerritorySelection } from "@/config/territory";
import DonutChart from "@/pages/proprietaire/components/DonutChart";
import StatCard from "@/pages/proprietaire/components/StatCard";
import { cn } from "@/utils/cn";
import type { ExportColumn } from "@/utils/export";
import { formatFcfa } from "@/utils/format";
import { useSuperAdmin } from "../data/SuperAdminProvider";
import { FORMULES } from "../data/types";
import { usePlatformData } from "@/store/platform/PlatformDataProvider";
import { useBilling } from "@/store/billing/BillingProvider";
import BarChart from "./components/BarChart";
import HorizontalBars from "./components/HorizontalBars";
import {
  DEFAULT_STATS_FILTERS,
  PERIODE_OPTIONS,
  buildSuperAdminStats,
  type PeriodeKey,
  type StatsFilters,
} from "./stats";

type Tone = "primary" | "accent" | "secondary" | "neutral";

const TONE_CLASSES: Record<"primary" | "accent" | "secondary", string> = {
  primary: "bg-primary-50 text-primary-600",
  accent: "bg-accent-50 text-accent-700",
  secondary: "bg-secondary-50 text-secondary-600",
};

interface KpiItem {
  icon: string;
  label: string;
  value: string;
  tone: Tone;
  hint?: string;
}

function SectionCard({
  icon,
  tone,
  title,
  subtitle,
  children,
}: {
  icon: string;
  tone: "primary" | "accent" | "secondary";
  title: string;
  subtitle?: string;
  children: ReactNode;
}) {
  return (
    <section className="flex flex-col rounded-lg border border-background-200 bg-background-50">
      <header className="flex items-center gap-2.5 border-b border-background-200 px-4 py-3.5 md:px-5">
        <span className={cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-md", TONE_CLASSES[tone])}>
          <i className={cn(icon, "text-base")} aria-hidden="true" />
        </span>
        <div>
          <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">{title}</h3>
          {subtitle && <p className="text-xs text-foreground-500">{subtitle}</p>}
        </div>
      </header>
      <div className="p-4 md:p-5">{children}</div>
    </section>
  );
}

function KpiGroup({ title, icon, items }: { title: string; icon: string; items: KpiItem[] }) {
  return (
    <section className="flex flex-col gap-3.5">
      <h3 className="flex items-center gap-2 font-heading text-sm font-semibold text-foreground-950 md:text-base">
        <i className={cn(icon, "text-base text-foreground-400")} aria-hidden="true" />
        {title}
      </h3>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {items.map((item) => (
          <StatCard
            key={item.label}
            icon={item.icon}
            label={item.label}
            value={item.value}
            tone={item.tone}
            hint={item.hint}
          />
        ))}
      </div>
    </section>
  );
}

export default function SuperAdminStatistiquesPage() {
  const { comptes } = useSuperAdmin();
  const { immeubles, biens, locataires, contrats } = usePlatformData();
  const { abonnements, paiements } = useBilling();
  const [filters, setFilters] = useState<StatsFilters>(DEFAULT_STATS_FILTERS);

  const shared = useMemo(
    () => ({
      immeubles: immeubles.length,
      biens: biens.length,
      locataires: locataires.length,
      contratsActifs: contrats.filter((contrat) => contrat.statut === "actif").length,
      abonnements,
      paiements,
    }),
    [immeubles, biens, locataires, contrats, abonnements, paiements]
  );

  const stats = useMemo(() => buildSuperAdminStats(comptes, filters, shared), [comptes, filters, shared]);
  const { kpis } = stats;

  const territory: TerritorySelection = {
    region: filters.region,
    province: filters.province,
    commune: filters.commune,
    secteur: filters.secteur,
  };

  const typeOptions = [
    { value: "", label: "Tous les types de compte" },
    { value: "proprietaire", label: "Propriétaires" },
    { value: "agence", label: "Agences" },
    { value: "utilisateur", label: "Utilisateurs" },
  ];

  const formuleOptions = [
    { value: "", label: "Toutes les formules" },
    ...FORMULES.map((formule) => ({ value: formule.id, label: formule.nom })),
  ];

  const update = (patch: Partial<StatsFilters>) => setFilters((prev) => ({ ...prev, ...patch }));
  const setTerritory = (next: TerritorySelection) => update({ ...next });
  const isFiltering =
    filters.periode !== DEFAULT_STATS_FILTERS.periode ||
    Boolean(filters.region || filters.province || filters.commune || filters.secteur || filters.type || filters.formuleId);

  const exportRows = [
    { label: "Nombre total d'utilisateurs", value: String(kpis.totalUtilisateurs) },
    { label: "DG Propriétaires", value: String(kpis.proprietaires) },
    { label: "DG Agences", value: String(kpis.agences) },
    { label: "Employés", value: String(kpis.employes) },
    { label: "Immeubles", value: String(kpis.immeubles) },
    { label: "Biens", value: String(kpis.biens) },
    { label: "Locataires", value: String(kpis.locataires) },
    { label: "Contrats actifs", value: String(kpis.contratsActifs) },
    { label: "Abonnements actifs", value: String(kpis.abonnementsActifs) },
    { label: "Abonnements en attente", value: String(kpis.abonnementsEnAttente) },
    { label: "Abonnements expirés", value: String(kpis.abonnementsExpires) },
    { label: "Revenus des abonnements (FCFA)", value: formatFcfa(kpis.revenusAbonnements) },
    { label: "Paiements en attente", value: String(kpis.paiementsEnAttente) },
    { label: "Paiements validés", value: String(kpis.paiementsValides) },
    { label: "Comptes actifs", value: String(kpis.comptesActifs) },
    { label: "Comptes suspendus", value: String(kpis.comptesSuspendus) },
  ];
  const exportColumns: ExportColumn<{ label: string; value: string }>[] = [
    { header: "Indicateur", value: (row) => row.label },
    { header: "Valeur", value: (row) => row.value },
  ];

  const inscriptionGroups = stats.months.map((month, index) => ({
    key: month.key,
    label: month.label,
    values: [stats.inscriptions[index]],
  }));

  const abonnementGroups = stats.months.map((month, index) => ({
    key: month.key,
    label: month.label,
    values: [stats.abonnementsActifs[index], stats.abonnementsEnAttente[index]],
  }));

  const revenuGroups = stats.months.map((month, index) => ({
    key: month.key,
    label: month.label,
    values: [stats.revenus[index]],
  }));

  const countFormatter = (value: number) => String(value);

  return (
    <div className="flex flex-col gap-6">
      <div className="max-w-3xl">
        <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">Statistiques</h2>
        <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">
          Un tableau de bord complet de la plateforme : comptes, patrimoine, abonnements et revenus. Tous les chiffres
          sont calculés à partir des données réelles du Super Admin et se mettent à jour automatiquement.
        </p>
      </div>

      <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
        <TerritoryFilters idPrefix="stat" value={territory} onChange={setTerritory} />

        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="stat-periode" className="text-xs font-label font-medium text-foreground-700">
              Période
            </label>
            <Select
              id="stat-periode"
              value={filters.periode}
              onChange={(event) => update({ periode: event.target.value as PeriodeKey })}
              options={PERIODE_OPTIONS}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="stat-type" className="text-xs font-label font-medium text-foreground-700">
              Type de compte
            </label>
            <Select
              id="stat-type"
              value={filters.type}
              onChange={(event) => update({ type: event.target.value })}
              options={typeOptions}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="stat-formule" className="text-xs font-label font-medium text-foreground-700">
              Formule
            </label>
            <Select
              id="stat-formule"
              value={filters.formuleId}
              onChange={(event) => update({ formuleId: event.target.value })}
              options={formuleOptions}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-label font-medium text-foreground-700">Export des indicateurs</span>
            <ExportMenu
              rows={exportRows}
              columns={exportColumns}
              filename="statistiques-super-admin"
              title="Statistiques Super Admin"
              subtitle="Indicateurs selon les filtres sélectionnés"
              className="[&>button]:w-full"
            />
          </div>
        </div>
        {isFiltering && (
          <div className="mt-4 flex items-center justify-between gap-3 border-t border-background-200 pt-3">
            <p className="text-xs text-foreground-500">
              {stats.kpis.totalUtilisateurs} compte{stats.kpis.totalUtilisateurs > 1 ? "s" : ""} correspondant aux
              filtres sélectionnés.
            </p>
            <button
              type="button"
              onClick={() => setFilters(DEFAULT_STATS_FILTERS)}
              className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-label font-medium text-foreground-600 transition-colors hover:text-foreground-950 cursor-pointer"
            >
              <i className="ri-refresh-line text-sm" aria-hidden="true" />
              Réinitialiser les filtres
            </button>
          </div>
        )}
      </section>

      {!stats.hasAnyCompte && (
        <EmptyState
          icon="ri-bar-chart-box-line"
          title="Aucune donnée à analyser"
          description="Les statistiques de la plateforme se construiront automatiquement dès que des comptes (propriétaires, agences, utilisateurs) seront créés."
        />
      )}

      <div className="flex flex-col gap-8">
        <KpiGroup
          title="Comptes & abonnements"
          icon="ri-group-line"
          items={[
            {
              icon: "ri-group-line",
              label: "Nombre total d'utilisateurs",
              value: String(kpis.totalUtilisateurs),
              tone: "primary",
            },
            { icon: "ri-user-follow-line", label: "Comptes actifs", value: String(kpis.comptesActifs), tone: "secondary" },
            { icon: "ri-user-forbid-line", label: "Comptes suspendus", value: String(kpis.comptesSuspendus), tone: "accent" },
            {
              icon: "ri-vip-diamond-line",
              label: "Abonnements actifs",
              value: String(kpis.abonnementsActifs),
              tone: "secondary",
            },
            {
              icon: "ri-time-line",
              label: "Abonnements en attente",
              value: String(kpis.abonnementsEnAttente),
              tone: "accent",
            },
            {
              icon: "ri-calendar-close-line",
              label: "Abonnements expirés",
              value: String(kpis.abonnementsExpires),
              tone: "neutral",
              hint: "Échéance de la période dépassée",
            },
          ]}
        />

        <KpiGroup
          title="Comptes par type"
          icon="ri-user-star-line"
          items={[
            { icon: "ri-user-star-line", label: "DG Propriétaires", value: String(kpis.proprietaires), tone: "primary" },
            { icon: "ri-building-2-line", label: "DG Agences", value: String(kpis.agences), tone: "accent" },
            { icon: "ri-team-line", label: "Employés", value: String(kpis.employes), tone: "secondary" },
          ]}
        />

        <KpiGroup
          title="Patrimoine"
          icon="ri-building-4-line"
          items={[
            { icon: "ri-building-2-line", label: "Immeubles", value: String(kpis.immeubles), tone: "secondary" },
            { icon: "ri-home-4-line", label: "Biens", value: String(kpis.biens), tone: "primary" },
            { icon: "ri-user-3-line", label: "Locataires", value: String(kpis.locataires), tone: "accent" },
            { icon: "ri-file-text-line", label: "Contrats actifs", value: String(kpis.contratsActifs), tone: "neutral" },
          ]}
        />

        <KpiGroup
          title="Paiements & revenus"
          icon="ri-hand-coin-line"
          items={[
            {
              icon: "ri-money-cny-circle-line",
              label: "Revenus des abonnements",
              value: formatFcfa(kpis.revenusAbonnements),
              tone: "primary",
            },
            { icon: "ri-checkbox-circle-line", label: "Paiements validés", value: String(kpis.paiementsValides), tone: "secondary" },
            { icon: "ri-hourglass-line", label: "Paiements en attente", value: String(kpis.paiementsEnAttente), tone: "accent" },
          ]}
        />
      </div>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
        <SectionCard
          icon="ri-user-add-line"
          tone="primary"
          title="Évolution des inscriptions"
          subtitle="Nouveaux comptes créés par mois."
        >
          <BarChart
            groups={inscriptionGroups}
            series={[{ key: "inscriptions", label: "Nouveaux comptes", token: "primary-500" }]}
            valueFormatter={countFormatter}
          />
        </SectionCard>

        <SectionCard
          icon="ri-vip-diamond-line"
          tone="accent"
          title="Évolution des abonnements"
          subtitle="Abonnements actifs et en attente par mois."
        >
          <BarChart
            groups={abonnementGroups}
            series={[
              { key: "actifs", label: "Actifs", token: "secondary-500" },
              { key: "attente", label: "En attente", token: "accent-500" },
            ]}
            valueFormatter={countFormatter}
          />
        </SectionCard>
      </div>

      <SectionCard
        icon="ri-line-chart-line"
        tone="secondary"
        title="Évolution des revenus"
        subtitle="Revenus des abonnements encaissés par mois (FCFA)."
      >
        <BarChart
          groups={revenuGroups}
          series={[{ key: "revenus", label: "Revenus (FCFA)", token: "primary-500" }]}
          valueFormatter={(value) => formatFcfa(value)}
        />
      </SectionCard>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
        <SectionCard
          icon="ri-pie-chart-2-line"
          tone="accent"
          title="Répartition Propriétaires / Agences"
          subtitle="Comptes par type."
        >
          <DonutChart
            slices={stats.parType}
            centerValue={String(kpis.totalUtilisateurs)}
            centerLabel="comptes"
            formatter={countFormatter}
            emptyLabel="Aucun compte à afficher."
          />
        </SectionCard>

        <SectionCard
          icon="ri-bar-chart-horizontal-line"
          tone="primary"
          title="Répartition des abonnements par formule"
          subtitle="Nombre de comptes par formule d'abonnement."
        >
          <HorizontalBars
            items={stats.parFormule}
            valueFormatter={countFormatter}
            token="primary-500"
            emptyLabel="Aucune formule attribuée."
          />
        </SectionCard>
      </div>

      <SectionCard
        icon="ri-map-pin-2-line"
        tone="secondary"
        title="Répartition géographique par région"
        subtitle="Nombre de comptes par région (déduite de la ville du compte)."
      >
        <HorizontalBars
          items={stats.parRegion}
          valueFormatter={countFormatter}
          token="secondary-500"
          emptyLabel="Aucune région à afficher pour les filtres sélectionnés."
        />
      </SectionCard>
    </div>
  );
}